import { useState, useEffect, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { HEADER, NAVBAR } from '../../config';
import NavbarVertical from './navbar/NavbarVertical';
import DashboardHeader from './header';
import { useSubscriberOrgs, useSubscriberProfile } from '@subflow/data-access';
import { setOrganizationSubdomain } from '@subflow/data-access';
import { useRouter } from 'next/router';
import { PATH_SUBSCRIBER } from '@subflow-frontend/routes/paths';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { useSessionAuth } from '@subflow-frontend/hooks/useSessionAuth';
import { PERSONAL_ORGANIZATION_ID } from 'libs/data-access/src/utils/constants';
import { SubscriberBottomNavigation } from '@subflow-frontend/layouts/subscriber/SubscriberBottomNavigation';
import { preloadAssets } from '@subflow-frontend/utils/preloadAssets';

type MainStyleProps = {
  collapseClick: boolean;
  disableTopPadding?: boolean;
  disableHorizontalPadding?: boolean;
  paddingBottom?: number;
};

const MainStyle = styled('main', {
  shouldForwardProp: (prop) =>
    prop !== 'collapseClick' && prop !== 'disableHorizontalPadding',
})<MainStyleProps>(
  ({ collapseClick, disableHorizontalPadding, paddingBottom, theme }) => ({
    flexGrow: 1,
    paddingTop: HEADER.MOBILE_HEIGHT,
    paddingBottom: HEADER.MOBILE_HEIGHT + 24,
    [theme.breakpoints.up('lg')]: {
      paddingLeft: disableHorizontalPadding ? 0 : 16,
      paddingRight: disableHorizontalPadding ? 0 : 16,
      paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT,
      paddingBottom:
        paddingBottom || paddingBottom === 0
          ? paddingBottom
          : HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
      width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
      transition: theme.transitions.create('margin-left', {
        duration: theme.transitions.duration.shorter,
      }),
      ...(collapseClick && {
        marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
      }),
    },
  })
);

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
  disableHorizontalPadding?: boolean;
  hideOrgSwitcher?: boolean;
};

export default function SubscriberLayout({
  children,
  disableHorizontalPadding,
  hideOrgSwitcher,
  ...rest
}: Props) {
  const { data: subscriberProfile } = useSubscriberProfile();
  const { collapseClick } = useCollapseDrawer();
  const { data: orgs } = useSubscriberOrgs();
  const { asPath, push } = useRouter();
  const { loading, isAuthenticated, user } = useSessionAuth();
  useEffect(() => {
    if (subscriberProfile?.avatar) {
      preloadAssets([subscriberProfile.avatar]);
    }
  }, [subscriberProfile?.avatar]);

  useEffect(() => {
    const orgImages = orgs?.map((org) => org.logo).filter((image) => !!image);
    if (orgImages) {
      preloadAssets(orgImages);
    }
  }, [orgs]);

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      datadogRum.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
      });
      datadogLogs.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
      });
    }
  }, [isAuthenticated, loading, user]);

  useEffect(() => {
    if (!asPath || !orgs) return;

    if (orgs.length === 0) {
      setOrganizationSubdomain(PERSONAL_ORGANIZATION_ID);
      return;
    }

    if (asPath.startsWith(PATH_SUBSCRIBER.settings.root)) {
      return;
    }

    const urlOrg = asPath.split('/')?.[2];
    if (urlOrg && urlOrg !== PERSONAL_ORGANIZATION_ID) {
      const matchedOrg = orgs.find((org) => org.subDomain === urlOrg);
      setOrganizationSubdomain(
        matchedOrg ? matchedOrg.subDomain : orgs[0].subDomain
      );
    } else {
      setOrganizationSubdomain(orgs[0].subDomain);
      push(`/sub/${orgs[0].subDomain}/dashboard`);
    }
  }, [orgs, asPath]);

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
        background: '#F4F9FF',
      }}
    >
      <DashboardHeader />

      <MainStyle
        collapseClick={collapseClick}
        disableHorizontalPadding={disableHorizontalPadding}
        sx={{
          minHeight: '100vh',
          pb: '12px !important',
        }}
        {...rest}
      >
        {children}
      </MainStyle>
      <SubscriberBottomNavigation />
    </Box>
  );
}
